import React, { useState, useEffect } from 'react';
import BackgroundImg from '../Assets/about_section_image-2.jpg';
import { 
  FaUserCircle, 
  FaPhone, 
  FaMapMarkerAlt, 
  FaCog,
  FaQuestionCircle,
  FaSignOutAlt,
  FaChevronRight,
  FaCreditCard
} from 'react-icons/fa';
import './Profile.css';
import api from '../api';
import { User } from '../api.response';
import useLogout from '../hook/useLogout';
import Loader from '../components/Loader';
import { useNavigate } from 'react-router-dom';

interface SettingsItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path?: string;
  isDestructive?: boolean;
  handler?: () => void;
}

const Profile: React.FC = () => {
  const [userData, setUserData] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const logout = useLogout();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.getProfile();
        
        if (!response.name) {
          navigate('/auth/signup', { state: { mobileNumber: response.phoneNumber, userId: response.id } });
          return;
        }
        
        setUserData(response);
      } catch (err) {
        console.error('Error fetching user profile:', err);
        setError('Failed to load profile data');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const [bankDetailsExpanded, setBankDetailsExpanded] = useState(false);

  const settingsItems: SettingsItem[] = [
    { id: 'address', label: 'Saved Address', icon: <FaMapMarkerAlt />, path: '/address' },
    { id: 'account', label: 'Manage Account', icon: <FaCog />, path: '/auth/signup', handler: () => navigate('/auth/signup', { state: { isEditMode: true } }) },
    // { id: 'faq', label: 'FAQ & Help', icon: <FaQuestionCircle />, path: '/profile/faq' },
    { id: 'logout', label: 'Logout', icon: <FaSignOutAlt />, isDestructive: true, handler: logout }
  ];

  const handleSettingClick = (item: SettingsItem) => {
    if (item.handler) {
      item.handler();
    } else if (item.path) {
      handleNavigation(item.path);
    }
  };

  if (error && !loading) {
    return (
      <div className="profile-screen">
        <div className="profile-container">
          <div className="error-message">
            <h2>Error Loading Profile</h2>
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>Retry</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-screen">
      <div className="profile-container">
        {/* Profile Header with Full Background Image */}
        <div className="profile-header">
          <div className="profile-header-bg">
            <img src={BackgroundImg} alt="Profile background" />
            <div className="bg-overlay"></div>
          </div>
          
          <div className="profile-content">
            <div className="profile-pic-section">
              {userData?.profilePicture ? (
                <img 
                  src={`https://api.flooron.in/${userData.profilePicture}`} 
                  alt="Profile" 
                  className="profile-picture"
                />
              ) : (
                <div className="profile-picture-placeholder">
                  <FaUserCircle size={100} />
                </div>
              )}
            </div>
            
            {loading ? (
              <div className="profile-info-loading">
                <Loader size="medium" type="spinner" text="Loading profile..." />
              </div>
            ) : (
              <div className="profile-info">
                <h1 className="profile-name">{userData?.name || 'Guest User'}</h1>
                {userData?.phoneNumber && (
                  <div className="contact-item">
                    <FaPhone className="contact-icon" />
                    <span>{userData.phoneNumber}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Settings Section */}
        <div className="settings-section">
          <h2 className="section-title">Settings</h2>
          <div className="settings-list">
            {/* Address */}
            <button 
              key="address"
              className="settings-item"
              onClick={() => handleSettingClick({ id: 'address', label: 'Saved Address', icon: <FaMapMarkerAlt />, path: '/address' })}
              disabled={loading}
            >
              <div className="settings-item-left">
                <div className="settings-icon-wrapper">
                  <FaMapMarkerAlt />
                </div>
                <span className="settings-label">Saved Address</span>
              </div>
              <FaChevronRight className="settings-arrow" />
            </button>
            
            {/* Manage Account */}
            <button 
              key="account"
              className="settings-item"
              onClick={() => handleSettingClick({ id: 'account', label: 'Manage Account', icon: <FaCog />, path: '/auth/signup', handler: () => navigate('/auth/signup', { state: { isEditMode: true } }) })}
              disabled={loading}
            >
              <div className="settings-item-left">
                <div className="settings-icon-wrapper">
                  <FaCog />
                </div>
                <span className="settings-label">Manage Account</span>
              </div>
              <FaChevronRight className="settings-arrow" />
            </button>
            
            {/* Bank Details Button */}
            <button 
              key="bank-details"
              className="settings-item"
              onClick={() => setBankDetailsExpanded(!bankDetailsExpanded)}
              disabled={loading}
            >
              <div className="settings-item-left">
                <div className="settings-icon-wrapper">
                  <FaCreditCard />
                </div>
                <span className="settings-label">Bank Details</span>
              </div>
              <FaChevronRight className={`settings-arrow ${bankDetailsExpanded ? 'rotated' : ''}`} />
            </button>
            
            {/* Bank Details Dropdown */}
            {bankDetailsExpanded && (
              <div className="bank-details-dropdown">
                <div className="bank-details-header">
                  <h3>FLOOR ON</h3>
                  <p>Kotak Mahindra Bank Ltd.</p>
                </div>
                <div className="bank-details-content">
                  <div className="bank-detail-row">
                    <span className="bank-detail-label">OD Account Number</span>
                    <span className="bank-detail-value">1 5 4 8 3 7 4 4 1 7</span>
                  </div>
                  <div className="bank-detail-row">
                    <span className="bank-detail-label">City Branch</span>
                    <span className="bank-detail-value">Indore</span>
                  </div>
                  <div className="bank-detail-row">
                    <span className="bank-detail-label">IFSC CODE</span>
                    <span className="bank-detail-value">K K B K 0 0 0 0 7 5 1</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Logout */}
            <button 
              key="logout"
              className="settings-item destructive"
              onClick={() => handleSettingClick({ id: 'logout', label: 'Logout', icon: <FaSignOutAlt />, isDestructive: true, handler: logout })}
              disabled={loading}
            >
              <div className="settings-item-left">
                <div className="settings-icon-wrapper">
                  <FaSignOutAlt />
                </div>
                <span className="settings-label">Logout</span>
              </div>
              <FaChevronRight className="settings-arrow" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;